/**
 * React Query mutation hook for upserting a single component prop label.
 *
 * Fired from the inline pill editor on the config page. The server uses
 * `updateOrCreate` keyed on `(tenant_id, component_id, prop_name)`, so
 * one endpoint covers both create and update.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  componentRulesService,
  type ComponentPropLabelDTO,
} from '../../api/componentRulesService';
import { componentPropLabelsKeys } from './queryKeys';

/** Variables accepted by `useUpsertComponentPropLabel().mutate(...)`. */
export interface UpsertComponentPropLabelVariables {
  componentId: string;
  propName: string;
  displayLabel: string;
}

/** Mutation hook for `PUT /claimflow/component-prop-labels`. */
export function useUpsertComponentPropLabel(): UseMutationResult<
  ComponentPropLabelDTO,
  Error,
  UpsertComponentPropLabelVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      componentId,
      propName,
      displayLabel,
    }: UpsertComponentPropLabelVariables) =>
      componentRulesService.upsertPropLabel(componentId, propName, displayLabel),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentPropLabelsKeys.list(variables.componentId),
      });
    },
  });
}
